import React, { useState, useEffect } from 'react';

import NamedAvatar from 'components/NamedAvatar/NamedAvatar';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  StyledBox,
  StyledDivider,
  StyledGrid,
  StyledLinearProgress,
  StyledTypography,
  StyledBorderedPaper,
} from 'styles/common';

import { GOAL_PROGRESSES, MOST_LEAST_ACTIVE_EMPLOYEE_META } from 'constants/ProjectConstants';
import { ArrowDownwardIcon, ArrowUpwardIcon } from 'constants/icons';
import { sendClickedMaxProgressOfGoalsEvent, sendClickedMinProgressOfGoalsEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { getTopPerformers } from 'services/analytics/goal';

import { Typography2 } from 'uiToolkit/Typography';

import { CircularLoader, ErrorMessage, MinMaxChartHeader, LabeledColoredCount } from '../../commonComponents';
import { AbsCenterContainer, NoDataContainer } from '../../styles';
import { useGoalAnalytics } from '../GoalAnalyticsContext';

const iconsList = [
  {
    id: 'MOST',
    icon: <ArrowUpwardIcon size="tinierFixed" color={theme.palette.icons.grey2} />,
  },
  {
    id: 'LEAST',
    icon: <ArrowDownwardIcon size="tinierFixed" color={theme.palette.icons.grey2} />,
  },
];

/**
 * @deprecated 06-08-2024
 */
const TopAndLeastPerformantEmployeeChart = () => {
  const [loading, setLoading] = useState(true);

  const [headers, setHeaders] = useState(null);
  const [data, setData] = useState(null);

  const [activityFrequency, setActivityFrequency] = useState('MOST');

  const dispatch = useDispatch();

  const {
    state: {
      currentState: { selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods },
    },
  } = useGoalAnalytics();

  const fetchData = () => {
    setLoading(true);
    setData(null);

    getTopPerformers({
      cohort: selectedCohort,
      groupByKeys: selectedGroupByKey,
      splitByKeys: selectedSplitByKey,
      orderType: activityFrequency === 'MOST' ? 'DESC' : 'ASC',
      timeBounds: selectedTimeBoundPeriods,
    })
      .then((res) => {
        console.log(res);
        const { columns, values } = res;

        setHeaders(columns);
        setData(values);
      })
      .catch((err) => {
        console.log(err || 'Some error has occurred while fetching TopLeast Performant Employees.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (selectedCohort !== null && selectedGroupByKey !== null && selectedSplitByKey !== null) fetchData();
  }, [selectedCohort, selectedGroupByKey, selectedSplitByKey, selectedTimeBoundPeriods, activityFrequency]);

  return (
    <StyledBorderedPaper style={{ height: '100%' }}>
      <MinMaxChartHeader
        heading={MOST_LEAST_ACTIVE_EMPLOYEE_META[activityFrequency].heading}
        subHeading={MOST_LEAST_ACTIVE_EMPLOYEE_META[activityFrequency].subHeading}
        selectedIconId={activityFrequency}
        updateSelectedIconId={(activityFreq) => {
          if (activityFreq === 'MOST') {
            sendClickedMaxProgressOfGoalsEvent();
          } else {
            sendClickedMinProgressOfGoalsEvent();
          }
          setActivityFrequency(activityFreq);
        }}
        iconsList={iconsList}
        toggleSwitchHeight="2.5rem"
        display="flex"
      />
      <StyledDivider leftGutter={4} rightGutter={4} />
      <StyledBox minHeight="140px" position="relative">
        {loading ? (
          <CircularLoader />
        ) : data === null ? (
          <ErrorMessage errorText="failed to fetch data!" />
        ) : data.length === 0 ? (
          <AbsCenterContainer>
            <NoDataContainer>
              <Typography2 color={theme.palette.grey.dark}>No data found in this cohort and timeline</Typography2>
            </NoDataContainer>
          </AbsCenterContainer>
        ) : (
          <EmployeeTableData headers={headers} data={data} activityFrequency={activityFrequency} />
        )}
      </StyledBox>
    </StyledBorderedPaper>
  );
};

const EmployeeTableData = ({ headers, activityFrequency, data }) => (
  <StyledBox padding="1.5rem 2rem">
    {activityFrequency === 'MOST' ? (
      <>
        {data.map((dObj, index) => (
          <MostActiveEmployeeRow key={`most-active-employee-${index}`} headers={headers} dataObj={dObj} />
        ))}
      </>
    ) : (
      <>
        {data.map((dObj, index) => (
          <LeastActiveEmployeeRow key={`least-active-employee-${index}`} headers={headers} dataObj={dObj} />
        ))}
      </>
    )}
  </StyledBox>
);

const MostActiveEmployeeRow = ({ headers, dataObj }) => (
  <StyledGrid container alignItems="center" bottomGutter={2}>
    {headers.map((hObj) => {
      const { id, displayName } = hObj;

      switch (displayName) {
        case 'Employee': {
          const { imageUrl, value } = dataObj[id];

          return (
            <StyledGrid item xs={4}>
              <NamedAvatar imageUrl={imageUrl} displayName={value} tooltipText="" />
            </StyledGrid>
          );
        }
        case 'Check In': {
          const { value: checkInCount } = dataObj[id];
          return (
            <StyledGrid container item xs={4}>
              <StyledLabeledColoredCount
                labelText="Check ins:"
                count={checkInCount}
                bgColor={theme.palette.background.lightBlue}
                textColor={theme.palette.primary.main}
                justify="center"
              />
            </StyledGrid>
          );
        }
        case 'Progress Level': {
          const { value: completionPercentage, progressLevel } = dataObj[id];
          const color = GOAL_PROGRESSES.filter((gProgress) => gProgress.value === progressLevel)?.[0]?.color;

          return (
            <StyledGrid container item xs={4}>
              <StyledGrid container alignItems="center" item xs={9}>
                <StyledLinearProgress
                  variant="determinate"
                  color="primary"
                  barColor={color}
                  value={completionPercentage}
                  fullWidth
                  height="0.75rem"
                />
              </StyledGrid>
              <StyledGrid container alignItems="center" justify="flex-end" item xs={3}>
                <StyledTypography variant="body2" color={color}>
                  {`${completionPercentage}%`}
                </StyledTypography>
              </StyledGrid>
            </StyledGrid>
          );
        }
        default:
          return '';
      }
    })}
  </StyledGrid>
);

const LeastActiveEmployeeRow = ({ headers, dataObj }) => (
  <StyledGrid container alignItems="center" bottomGutter={2}>
    {headers.map((hObj) => {
      const { id, displayName } = hObj;

      switch (displayName) {
        case 'Employee': {
          const { imageUrl, value } = dataObj[id];

          return (
            <StyledGrid item xs={4}>
              <NamedAvatar imageUrl={imageUrl} displayName={value} tooltipText="" />
            </StyledGrid>
          );
        }
        case 'Check In': {
          const { value: checkInCount } = dataObj[id];
          return (
            <StyledGrid container item xs={3}>
              <StyledLabeledColoredCount
                labelText="Check ins:"
                count={checkInCount}
                bgColor={theme.palette.background.lightBlue}
                textColor={theme.palette.primary.main}
                justify="flex-end"
              />
            </StyledGrid>
          );
        }
        case 'Last Updated': {
          const { value: lastUpdated } = dataObj[id];

          return (
            <StyledGrid container alignItems="center" justify="center" item xs={5}>
              <StyledTypography variant="body2" color="textSecondary">
                {lastUpdated}
              </StyledTypography>
            </StyledGrid>
          );
        }
        default:
          return '';
      }
    })}
  </StyledGrid>
);

const StyledLabeledColoredCount = styled(LabeledColoredCount)`
  justify-content: ${(props) => (props.justify ? props.justify : 'flex-start')};
`;

export default TopAndLeastPerformantEmployeeChart;
